import { createBrowserRouter } from "react-router-dom"
import {
  fieldAuditsTableEnabled,
  amPortalRemindersEnabled,
  businessDevAmPortalEnabled,
  riskMitigationEnabled
} from "./utilities/featureFlags"

import Dashboard from './components/Dashboard'
import Login from './components/Login'
import PasswordRecovery from './components/PasswordRecovery'
import UnitPage from './components/Dealer/UnitPage/UnitPage'
import NotFound from './components/NotFound'
import AccountManagement from './components/AccountManagement/AccountManagement'
import ResetPassword from './components/ResetPassword'
import TransferHistory from './components/Dealer/TransferHistory/TransferHistory'
import Blackbook from './components/Dealer/Blackbook/Blackbook'
import ACHAuthorization from './components/AccountManagement/ACHAuthorization'
import NAP from './components/Dealer/NAP/NAP'
import BBSearchResult from './components/Dealer/Blackbook/BBSearchResult'
import Notifications from "./components/Notifications/Notifications"
import Root from "./components/Root"
import MSALRedirect from "./components/EmployeeLogin/MSALRedirect"
import CustomerSupport from "./components/Dealer/CustomerSupport/CustomerSupport"
import RiskPage from "./components/AccountManager/Risk/RiskPage"
import TermsOfService from "./components/Dealer/TermsOfService/TermsOfService"
import TransferPage from "./components/Dealer/TransferPage/TransferPage"
import Reminders from "./components/AccountManager/Reminders/Reminders"
import DealerEdit from "./components/AccountManager/Dealer/DealerEdit"
import BusinessDevPage from "./components/AccountManager/BusinessDevelopment/BusinessDevPage"
import ReminderPage from "./components/AccountManager/Reminders/ReminderPage"
import MobileReserveInfo from "./components/AccountManager/Reserve/MobileReserveInfo"
import TestError from "./components/TestError"
import LoginSupport from './components/LoginSupport'
import MobileAppPage from "./components/AccountManager/Apps/MobileAppPage"
import NotificationDetail from "./components/Notifications/NotificationDetail"
import MobileProspectiveDealerPage from "./components/AccountManager/Dealers/MobileProspectDealerPage"
import AuditsPage from "./components/AccountManager/Audits/AuditsPage"
import MobileNotesPage from "./components/AccountManager/Notes/MobileNotesPage"
import AmDealerDashboard from "./components/AccountManager/Dealer/AmDealerDashboard"
import AcceptInvitation from "./components/AcceptInvitation"
import CouponInfoPage from "./components/Dealer/Coupons/CouponInfoPage"
import FieldAudit from "./components/VCI/FieldAudit/FieldAudit"
import AuditUnit from "./components/VCI/AuditUnit/AuditUnit"
import SummaryOfRates from "./components/Dealer/TermsOfService/SummaryOfRates"
import TxnPage from "./components/Dealer/TransferPage/TxnPage"
import VciDashboard from "./components/VCI/VCIDashboard"
import CarpoolUnitsPage from "./components/Carpool/Units/CarpoolUnitsPage"
import CarpoolUnitPage from "./components/Carpool/Units/CarpoolUnitPage"
import CarpoolQuotesPage from "./components/Carpool/Quotes/CarpoolQuotesPage"
import CarpoolQuotePage from "./components/Carpool/Quotes/CarpoolQuotePage"
import CarpoolOrdersPage from "./components/Carpool/Orders/CarpoolOrdersPage"
import CarpoolOrderPage from "./components/Carpool/Orders/CarpoolOrderPage"
import TransportationLayout from "./components/Carpool/TransportationLayout"
import TrailerLocation from "./components/TrailerLocation/TrailerLocation"

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      {
        path: '/',
        element: <Dashboard />
      },
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'unit/:unitId',
        element: <UnitPage />
      },
      {
        path: 'account',
        element: <AccountManagement />
      },
      ...(riskMitigationEnabled
        ? [
          {
            path: 'risk',
            element: <RiskPage />
          }
        ]
        : []
      ),
      {
        path: 'summary_of_rates',
        element: <SummaryOfRates />
      },
      {
        path: 'transfers',
        children: [
          {
            path: '',
            element: <TransferHistory />
          },
          {
            path: ':transfer_id',
            element: <TransferPage />
          }
        ]
      },
      {
        path: 'txns',
        children: [
          {
            path: ':txnId',
            element: <TxnPage />
          }
        ]
      },
      {
        path: 'blackbook',
        children: [
          {
            path: '',
            element: <Blackbook />
          },
          {
            path: 'data',
            element: <BBSearchResult />
          }
        ]
      },
      {
        path: 'transportation',
        element: <TransportationLayout />,
        children: [
          {
            path: '',
            element: <CarpoolUnitsPage />
          },
          {
            path: 'quotes',
            element: <CarpoolQuotesPage />
          },
          {
            path: 'quotes/:quoteId',
            element: <CarpoolQuotePage />
          },
          {
            path: 'orders',
            element: <CarpoolOrdersPage />
          },
          {
            path: 'orders/:orderId',
            element: <CarpoolOrderPage />
          },
          {
            path: ':unitId',
            element: <CarpoolUnitPage />
          }
        ]
      },
      {
        path: 'ach_auth',
        element: <ACHAuthorization />
      },
      {
        path: 'nap',
        element: <NAP />
      },
      {
        path: 'nap_edit',
        element: <NAP issues={true} />
      },
      {
        path: 'notifications',
        children: [
          {
            path: '',
            element: <Notifications />
          },
          {
            path: ':notification_id',
            element: <NotificationDetail />
          }
        ]
      },
      {
        path: 'customer_support',
        element: <CustomerSupport />
      },
      {
        path: 'terms',
        element: <TermsOfService />
      },
      {
        path: 'dealers/:dealerId',
        children: [
          {
            path: '',
            element: <AmDealerDashboard />
          },
          {
            path: 'edit',
            element: <DealerEdit />
          },
          {
            path: 'transportation',
            element: <TransportationLayout />,
            children: [
              { path: '', element: <CarpoolUnitsPage /> },
              { path: 'quotes', element: <CarpoolQuotesPage /> },
              { path: 'quotes/:quoteId', element: <CarpoolQuotePage /> },
              { path: 'orders', element: <CarpoolOrdersPage /> },
              { path: 'orders/:orderId', element: <CarpoolOrderPage /> },
              { path: ':unitId', element: <CarpoolUnitPage /> },
            ]
          },
        ]
      },
      ...(amPortalRemindersEnabled
        ? [
          {
            path: 'reminders',
            children: [
              {
                path: '',
                element: <Reminders />
              },
              {
                path: ':reminderId',
                element: <ReminderPage />
              }
            ]
          }
        ]
        : []
      ),
      ...(businessDevAmPortalEnabled
        ? [
          {
            path: 'business_dev',
            element: <BusinessDevPage />
          },
          {
            path: 'apps/:appId',
            element: <MobileAppPage />
          },
        ]
        : []
      ),
      {
        path: 'reserve_info',
        element: <MobileReserveInfo />
      },
      {
        path: 'testerror',
        element: <TestError />
      },
      {
        path: 'prospects/:dealerId',
        element: <MobileProspectiveDealerPage />
      },
      ...(fieldAuditsTableEnabled
        ? [
          {
            path: 'audits',
            element: <AuditsPage />
          }
        ]
        : []
      ),
      {
        path: 'field_audits',
        element: <VciDashboard />
      },
      {
        path: 'field_audits/:fieldAuditId',
        children: [
          {
            path: '',
            element: <FieldAudit />,
          },
          {
            path: 'audit_units/:auditUnitId',
            element: <AuditUnit />
          }
        ]
      },
      {
        path: 'notes/:type/:id',
        element: <MobileNotesPage />
      },
      {
        path: 'coupons/:id',
        element: <CouponInfoPage />
      },
      {
        path: 'locations/:locationId',
        element: <TrailerLocation />
      }
    ]
  },
  {
    path: 'accept-invitation',
    element: <AcceptInvitation />
  },
  {
    path: 'forgot-password',
    element: <PasswordRecovery />
  },
  {
    path: 'reset-password',
    element: <ResetPassword />
  },
  {
    path: 'msalauth',
    element: <MSALRedirect />
  },
  {
    path: 'loginsupport',
    element: <LoginSupport />
  },
  {
    path: '*',
    element: <NotFound />
  },
])

export default router